"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { HexColorPicker, RgbaColorPicker } from "react-colorful"

interface ColorPickerProps {
  isOpen: boolean
  onClose: () => void
  /** Optional — a field that has never been colour-coded has no Color, so callers legitimately
   *  pass nothing. Typed as optional so the compiler stops the next caller from assuming otherwise. */
  initialColor?: string | null
  onColorSelect: (color: string) => void
}

/** Neutral swatch used when a field arrives with no colour — matches the "+ Option" default. */
const DEFAULT_SWATCH = "#F5F5F5"

// Replace suggestedColors with the Figma pastel palette
const suggestedColors = [
  ["#F8C3C3", "#FFF4E0", "#EAF5FF", "#E6F7E8", "#F5EAFF", "#F2F2F2"],
  ["#F28686", "#FFE3AF", "#B2DAFF", "#B3F6BB", "#E0CDF2", "#E5E5E5"],
  ["#E01C1C", "#FFC968", "#6AB6FE", "#76D580", "#CBA9EA", "#CACACA"],
  ["#A20202", "#F1A92A", "#3695F2", "#51BB5D", "#B383DB", "#A5A5A5"],
]

// Utility functions for hex <-> rgb(a)
function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16)
        return hex.length === 1 ? "0" + hex : hex
      })
      .join("")
  )
}
function hexToRgb(hex: string) {
  if (!hex) return { r: 245, g: 245, b: 245 }
  let c = hex.replace('#', '')
  if (c.length === 3) c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2]
  if (c.length !== 6) return { r: 0, g: 0, b: 0 }
  return {
    r: parseInt(c.slice(0, 2), 16),
    g: parseInt(c.slice(2, 4), 16),
    b: parseInt(c.slice(4, 6), 16),
  }
}

export function ColorPicker({ isOpen, onClose, initialColor, onColorSelect }: ColorPickerProps) {
  const [rgba, setRgba] = useState<{ r: number; g: number; b: number; a: number }>(() => {
    // A field with no Color set (any Yes/No field, and any custom field never colour-coded) reaches
    // us with initialColor undefined. This runs inside the useState initialiser, so an unguarded
    // .startsWith() throws during render and takes the WHOLE microapp down with a client-side
    // exception — not just the picker. Fall back to the neutral swatch the "+ Option" button uses.
    if (!initialColor || typeof initialColor !== 'string') {
      const { r, g, b } = hexToRgb(DEFAULT_SWATCH)
      return { r, g, b, a: 1 }
    }

    // Try to parse initialColor as hex or rgba string
    if (initialColor.startsWith('rgba')) {
      const m = initialColor.match(/rgba?\((\d+), ?(\d+), ?(\d+),? ?([\d\.]+)?\)/)
      if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] ? +m[4] : 1 }
    }
    const { r, g, b } = hexToRgb(initialColor)
    return { r, g, b, a: 1 }
  })
  const selectedColor = rgbToHex(rgba)
  const opacity = Math.round(rgba.a * 100)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [mode, setMode] = useState<'hex' | 'rgb'>('hex')

  useEffect(() => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
    gradient.addColorStop(0, "white")
    gradient.addColorStop(0.5, "red")
    gradient.addColorStop(0.5, "blue")
    gradient.addColorStop(1, "black")

    // Fill with gradient
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }, [])

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true)
    handleCanvasMouseMove(e)
  }

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !canvasRef.current) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const imageData = ctx.getImageData(x, y, 1, 1).data
    const color = `rgb(${imageData[0]}, ${imageData[1]}, ${imageData[2]})`
    setRgba({ ...hexToRgb(color), a: rgba.a })
  }

  const handleCanvasMouseUp = () => {
    setIsDragging(false)
  }

  const handleColorSelect = (color: string) => {
    setRgba({ ...hexToRgb(color), a: rgba.a })
  }

  const handleSave = () => {
    let colorString = selectedColor
    if (mode === 'rgb') {
      colorString = rgba.a < 1 ? `rgba(${rgba.r},${rgba.g},${rgba.b},${rgba.a.toFixed(2)})` : `rgb(${rgba.r},${rgba.g},${rgba.b})`
    }
    onColorSelect(colorString)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]" aria-describedby="color-picker-description">
        <div id="color-picker-description" className="sr-only">
          Color picker dialog to select a color for the field
        </div>
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Choose colour</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col items-center gap-4">
            <div className="w-48 aspect-square mb-4 p-2 bg-white rounded shadow flex items-center justify-center">
              {mode === 'hex' ? (
                <HexColorPicker color={selectedColor} onChange={color => setRgba({ ...hexToRgb(color), a: 1 })} />
              ) : (
                <RgbaColorPicker color={rgba} onChange={setRgba} />
              )}
            </div>
            <div className="flex items-center gap-2 justify-center w-full">
              <select value={mode} onChange={e => setMode(e.target.value as 'hex' | 'rgb')} className="border border-gray-300 rounded px-2 py-1 text-sm">
                <option value="hex">Hex</option>
                <option value="rgb">RGB</option>
              </select>
              <div className="flex items-center gap-2">
                {mode === 'hex' ? (
                  <>
                    <input
                      type="text"
                      value={selectedColor}
                      onChange={e => setRgba({ ...hexToRgb(e.target.value), a: 1 })}
                      className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                      maxLength={7}
                    />
                  </>
                ) : (
                  <>
                    <span className="text-xs text-gray-500">R</span>
                    <input type="number" min={0} max={255} value={rgba.r} onChange={e => setRgba({ ...rgba, r: +e.target.value })} className="w-12 border border-gray-300 rounded px-2 py-1 text-sm" />
                    <span className="text-xs text-gray-500">G</span>
                    <input type="number" min={0} max={255} value={rgba.g} onChange={e => setRgba({ ...rgba, g: +e.target.value })} className="w-12 border border-gray-300 rounded px-2 py-1 text-sm" />
                    <span className="text-xs text-gray-500">B</span>
                    <input type="number" min={0} max={255} value={rgba.b} onChange={e => setRgba({ ...rgba, b: +e.target.value })} className="w-12 border border-gray-300 rounded px-2 py-1 text-sm" />
                  </>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2">Suggested</h3>
            <div className="grid grid-cols-6 gap-2">
              {suggestedColors.flat().map((color, index) => (
                <div
                  key={index}
                  className={`w-8 h-8 rounded cursor-pointer ${selectedColor === color ? "ring-2 ring-blue-500" : ""}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorSelect(color)}
                />
              ))}
            </div>
          </div>

          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">Colour choices with AA accessibility contrast level check</h3>
            <div className="grid grid-cols-6 gap-2">
              {suggestedColors.flat().map((color, index) => (
                <div
                  key={index}
                  className="w-8 h-8 rounded flex items-center justify-center text-xs font-medium"
                  style={{
                    backgroundColor: color,
                    color: 'black',
                  }}
                >
                  AB
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button variant="outline" className="mr-2" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Apply</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
